import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { db } from "../firebase";
import { ref, push, set, onValue, update } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import "../css/AskQuestion.css";

const AskQuestion = () => {
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [image, setImage] = useState(null);
  const [description, setDescription] = useState("");
  const [savedDescription, setSavedDescription] = useState(""); // ✅ save copy
  const [conversation, setConversation] = useState([]);
  const [questionId, setQuestionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWaitingRoom, setShowWaitingRoom] = useState(false);
  const [formConfirmed, setFormConfirmed] = useState(false);
  const [videoRequested, setVideoRequested] = useState(false);
  const [isMatched, setIsMatched] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [summary, setSummary] = useState("");
  const [showSummaryPopup, setShowSummaryPopup] = useState(false);

  const chatEndRef = useRef(null);
  const navigate = useNavigate();

  const geminiApiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const geminiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${geminiApiKey}`;

  const stemOptions = {
    Science: ["Physics", "Biology", "Chemistry"],
    Technology: ["Information Technology", "Robotics", "Digital Media"],
    Engineering: [
      "Civil Engineering",
      "Mechanical Engineering",
      "Electrical Engineering",
    ],
    Mathematics: ["Algebra", "Geometry", "Statistics"],
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation]);

  useEffect(() => {
    if (!questionId) return;
    const chatRef = ref(db, `chats/${questionId}`);
    return onValue(chatRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const sorted = Object.values(data).sort(
          (a, b) => a.timestamp - b.timestamp
        );
        setConversation(sorted);
      }
    });
  }, [questionId]);

  useEffect(() => {
    if (!questionId) return;
    const matchRef = ref(db, `waitingQuestions/${questionId}`);
    return onValue(matchRef, (snapshot) => {
      const data = snapshot.val();
      if (data?.matched) setIsMatched(true);
    });
  }, [questionId]);

  useEffect(() => {
    if (!questionId) return;
    const startCallRef = ref(db, `waitingQuestions/${questionId}/startCall`);
    return onValue(startCallRef, (snapshot) => {
      if (snapshot.exists() && snapshot.val() === true && !hasNavigated) {
        setHasNavigated(true);
        navigate(`/videocall?id=${questionId}`);
      }
    });
  }, [questionId, hasNavigated, navigate]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setSubcategory("");
  };

  const handleImageUpload = (e) => {
    setImage(e.target.files[0]);
  };

  const handleAskTeacher = async () => {
    setShowWaitingRoom(true);
    const questionsRef = ref(db, "waitingQuestions");
    const newQuestionRef = push(questionsRef);
    const newId = newQuestionRef.key;

    await set(newQuestionRef, {
      category,
      subcategory,
      description,
      timestamp: Date.now(),
      matched: false,
      videoRequested: false,
      startCall: false,
    });

    return newId;
  };

  const handleSubmit = async (text, imgFile, id) => {
    if (!text.trim() && !imgFile) return;
    setLoading(true);

    let base64Image = null;
    if (imgFile) {
      base64Image = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(imgFile);
      });
    }

    const userMsg = {
      sender: "student",
      text: text || "uploaded",
      image: base64Image || null,
      timestamp: Date.now(),
    };

    const chatRef = ref(db, `chats/${id}`);
    await set(push(chatRef), userMsg);

    if (!isMatched) {
      const prompt = `The student is asking a question under the topic of "${subcategory}". ${
        imgFile ? `(Image uploaded: ${imgFile.name})` : ""
      }\nDescription: ${text}\nPlease summarize and provide a solution. (Simplify it and make the answer easily understand. Below 60 words)`;

      try {
        const res = await axios.post(geminiUrl, {
          contents: [{ parts: [{ text: prompt }] }],
        });

        const aiReply =
          res.data.candidates?.[0]?.content?.parts?.[0]?.text ||
          "No response from AI.";

        const aiMsg = {
          sender: "ai",
          text: aiReply,
          timestamp: Date.now(),
        };

        await set(push(chatRef), aiMsg);
      } catch (error) {
        const errMsg = {
          sender: "ai",
          text: "❌ Gemini failed to respond.",
          timestamp: Date.now(),
        };
        await set(push(chatRef), errMsg);
      }
    }

    setDescription("");
    setImage(null);
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (!category || !subcategory || !description.trim()) {
      alert("Please complete category, sub-topic and description.");
      return;
    }

    setSavedDescription(description); // ✅ 保存问题副本
    setFormConfirmed(true);
    const newId = await handleAskTeacher();
    setQuestionId(newId);
    await handleSubmit(description, image, newId);
  };

  const handleRequestVideo = async () => {
    if (!questionId) return;
    const questionRef = ref(db, `waitingQuestions/${questionId}`);
    await update(questionRef, { videoRequested: true });
    alert("🎥 Video call request sent to the teacher.");
    setVideoRequested(true);
  };

  const handleMarkAsDone = async () => {
    const title = `${category} - ${subcategory}`;
    const fullText = conversation
      .map((msg) => {
        const sender =
          msg.sender === "student"
            ? "Student"
            : msg.sender === "teacher"
            ? "Teacher"
            : "AI";
        return `${sender}: ${msg.text}`;
      })
      .join("\n");

    const prompt = `Summarize the following STEM question and solution in a helpful way, suitable for a public forum:\nTitle: ${title}\nChat:\n${fullText}`;

    try {
      const res = await axios.post(geminiUrl, {
        contents: [{ parts: [{ text: prompt }] }],
      });

      const geminiSummary =
        res.data.candidates?.[0]?.content?.parts?.[0]?.text ||
        "No summary generated.";
      setSummary(geminiSummary);
      setShowSummaryPopup(true);
    } catch (err) {
      alert("❌ Gemini failed to summarize.");
      console.error(err);
    }
  };

  const handleConfirmSave = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        alert("❌ You must be logged in to save.");
        return;
      }

      const userId = user.uid;
      const userSummaryRef = ref(db, `summaries/${userId}`);
      await push(userSummaryRef, {
        title: `${category} - ${subcategory}`,
        question: savedDescription, // ✅ 使用副本，不是空的 description
        summary,
        timestamp: Date.now(),
      });

      alert("✅ Summary saved to your history.");
      setShowSummaryPopup(false);
      navigate("/history");
    } catch (err) {
      alert("❌ Failed to save.");
      console.error(err);
    }
  };

  return (
    <div className="ask-container">
      <h2 className="chat-title">TanyaNow</h2>

      {!formConfirmed && (
        <div className="form-bar">
          <select value={category} onChange={handleCategoryChange}>
            <option value="">🎯 Select Main Category</option>
            {Object.keys(stemOptions).map((main) => (
              <option key={main} value={main}>
                {main}
              </option>
            ))}
          </select>

          {category && (
            <select
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
            >
              <option value="">📚 Choose Sub-topic</option>
              {stemOptions[category].map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          )}

          <textarea
            placeholder="✍️ Describe your question here... Be as detailed as possible for better assistance."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <input
            type="file"
            onChange={handleImageUpload}
            accept="image/*"
            title="Upload an image to support your question"
          />
          <button onClick={handleConfirm}>
            <span>✅ Confirm to Start Chat</span>
          </button>
        </div>
      )}

      {formConfirmed && (
        <>
          {showWaitingRoom && (
            <div className="waiting-room">
              <h3>⏳ Waiting for a teacher to assist you...</h3>
              <p>
                You can continue chatting with AI while waiting for a human
                teacher.
              </p>
            </div>
          )}

          <div className="chat-box">
            {conversation.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.sender}`}>
                <div className="chat-bubble">
                  <strong>
                    {msg.sender === "student"
                      ? "👩‍🎓 Student"
                      : msg.sender === "teacher"
                      ? "👨‍🏫 Teacher"
                      : "🤖 AI Assistant"}
                  </strong>
                  {msg.image && (
                    <img
                      src={msg.image}
                      alt="uploaded"
                      className="chat-image"
                    />
                  )}
                  <p>{msg.text}</p>
                </div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          <div className="input-bar">
            <label className="upload-icon">
              📎
              <input
                type="file"
                onChange={handleImageUpload}
                accept="image/*"
              />
            </label>
            <textarea
              rows="1"
              placeholder="💬 Type your question here..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(description, image, questionId);
                }
              }}
            />
            <button
              onClick={() => handleSubmit(description, image, questionId)}
              disabled={loading}
              title={loading ? "Sending..." : "Send message"}
            >
              {loading ? "⏳" : "➤"}
            </button>
          </div>

          {!videoRequested && (
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button
                onClick={handleRequestVideo}
                style={{
                  backgroundColor: "#007bff",
                  color: "white",
                  padding: "15px 25px",
                  border: "none",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                🎥 Request Video Call with Teacher
              </button>
            </div>
          )}

          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <button
              onClick={handleMarkAsDone}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                padding: "15px 25px",
                border: "none",
                borderRadius: "12px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            >
              📌 Mark as Done & Save Summary
            </button>
          </div>
        </>
      )}

      {showSummaryPopup && (
        <div className="summary-popup">
          <div className="popup-content">
            <h3>📝 AI Generated Summary</h3>
            <p>{summary}</p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button onClick={handleConfirmSave}>✅ Save to History</button>
              <button onClick={() => setShowSummaryPopup(false)}>
                ❌ Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AskQuestion;